import { FormattedMessage } from 'react-intl';
import React from 'react';
import bemify from 'bemify-js';

import Code from '../components/Code';
import Paragraph from '../components/Paragraph';
import StepTitle from '../components/StepTitle';
import SyntaxHighlighter from '../components/SyntaxHighlighter';

const bem = bemify('project-structure');

const codeBlock = `github-example-app
├── .git
├── build
├── creates
│   └── issue.js
├── node_modules
├── test
│   ├── authentication.js
│   ├── issue.js
│   └── repo.js
├── triggers
│   ├── issue.js
│   └── repo.js
├── .env
├── .gitignore
├── .nvmrc
├── .travis.yml
├── .zapierapprc
├── .authentication.js
├── .index.js
└── .package.json`;

const ProjectStructure = () => (
  <div className={bem()}>
    <StepTitle>
      <FormattedMessage
        defaultMessage="Explore the Project: Project Structure"
        id="headings-explore-project-structure"
      />
    </StepTitle>
    <Paragraph>
      <FormattedMessage
        defaultMessage="Let's take a moment to look around the project."
        id="descriptions-project-structure-intro"
      />
    </Paragraph>
    <SyntaxHighlighter language="text">{codeBlock}</SyntaxHighlighter>
    <Paragraph>
      <FormattedMessage
        defaultMessage="If you've worked with Node.js before the project structure will look pretty familiar. Let's take a quick look at a couple of important files and directories in here:"
        id="descriptions-project-structure-nodejs-familiarity-overview"
      />
    </Paragraph>
    <Paragraph>
      <Code>package.json</Code>{' '}
      <FormattedMessage
        defaultMessage="is a standard npm file. There's nothing here you need to pay attention to right now, just know this file will be used later to upgrade to a new version of the Zapier CLI and to create new versions of your app."
        id="descriptions-package-json-file-purpose"
      />
    </Paragraph>
    <Paragraph>
      <FormattedMessage
        defaultMessage="In a few minutes we'll show you the"
        id="descriptions-zapier-push-command-intro-prefix"
      />{' '}
      <Code>
        <FormattedMessage
          defaultMessage="zapier push"
          id="labels-command-zapier-push"
        />
      </Code>{' '}
      <FormattedMessage
        defaultMessage="command which will register the app with the Zapier platform. At that point you'll see a"
        id="descriptions-zapier-push-command-app-registration-middle"
      />{' '}
      <Code>
        <FormattedMessage
          defaultMessage=".zapierapprc"
          id="labels-zapier-app-rc-file"
        />
      </Code>{' '}
      <FormattedMessage
        defaultMessage="file created. This holds your actual app id. This is not critical to know about right now, but when you collaborate with other developers on your team you'll share this file, and it's how you'll all be able to manage the same instance of the app. Again, it's not there yet - it'll get created in just a moment..."
        id="descriptions-zapier-app-rc-file-purpose-and-collaboration"
      />
    </Paragraph>
    <Paragraph>
      <FormattedMessage
        defaultMessage="The"
        id="descriptions-test-directory-overview-prefix"
      />{' '}
      <Code>
        <FormattedMessage
          defaultMessage="/test"
          id="labels-test-directory-path"
        />
      </Code>{' '}
      <FormattedMessage
        defaultMessage="directory contains all of our tests. Each will get run when we execute zapier test. We'll try this out in the next section."
        id="descriptions-test-directory-overview-suffix"
      />
    </Paragraph>
  </div>
);

export default ProjectStructure;
